import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  Eye,
  RotateCcw,
  X,
} from 'lucide-react';
import { activeLogApi } from './api';
import type { ActiveLog, ActiveLogAction, ActiveLogFilter } from './types';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input, Select } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { Button } from '@/components/ui/Button';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { useT } from '@/lib/i18n/useT';
import { useCanView } from '@/lib/permissions/useCanAccess';
import { useHasFeature } from '@/lib/permissions/useCompanyFeatures';
import { PaywallOverlay } from '@/components/ui/PaywallOverlay';

const ACTIONS: ActiveLogAction[] = [
  'create',
  'update',
  'delete',
  'view',
  'login',
  'logout',
  'export',
  'deploy',
  'other',
];

const PER_PAGE = 25;
const ENTITY_NAME = 'ActiveLog';

const ACTION_TONE: Record<ActiveLogAction, string> = {
  create: '#22C55E',
  update: '#F59E0B',
  delete: '#EF4444',
  view: 'var(--color-xk-text-muted)',
  login: '#3B82F6',
  logout: '#64748B',
  export: '#B262F5',
  deploy: 'var(--color-xk-accent)',
  other: 'var(--color-xk-text-muted)',
};

interface FilterFormState {
  q: string;
  action: ActiveLogAction | '';
  entity: string;
  userId: string;
  eventId: string;
  from: string;
  to: string;
}

const EMPTY_FILTER: FilterFormState = {
  q: '',
  action: '',
  entity: '',
  userId: '',
  eventId: '',
  from: '',
  to: '',
};

export function ActiveLogPage() {
  const t = useT();
  useFavoritable(t('activeLog.title'));
  const canView = useCanView(ENTITY_NAME);
  const hasFeature = useHasFeature('activeLog');

  const [form, setForm] = useState<FilterFormState>(EMPTY_FILTER);
  // `applied` drives the query — separate from the form so typing in inputs
  // doesn't refetch on every keystroke.
  const [applied, setApplied] = useState<FilterFormState>(EMPTY_FILTER);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ActiveLog | null>(null);

  const filter: ActiveLogFilter = useMemo(() => {
    const f: ActiveLogFilter = {};
    if (applied.q.trim()) f.q = applied.q.trim();
    if (applied.action) f.action = applied.action;
    if (applied.entity.trim()) f.entity = applied.entity.trim();
    if (applied.userId.trim()) {
      const n = parseInt(applied.userId.trim(), 10);
      if (Number.isFinite(n)) f.userId = n;
    }
    if (applied.eventId.trim()) {
      const n = parseInt(applied.eventId.trim(), 10);
      if (Number.isFinite(n)) f.eventId = n;
    }
    if (applied.from) f.from = new Date(applied.from).toISOString();
    if (applied.to) {
      // Treat the date picker's day as inclusive — push to end of day.
      const d = new Date(applied.to);
      d.setHours(23, 59, 59, 999);
      f.to = d.toISOString();
    }
    return f;
  }, [applied]);

  const listQ = useQuery({
    queryKey: ['active-log', filter, page] as const,
    queryFn: () =>
      activeLogApi.list({
        filter,
        page,
        perPage: PER_PAGE,
        sort: 'createdAt',
        order: 'DESC',
      }),
    enabled: canView && hasFeature,
    placeholderData: (prev) => prev,
  });

  if (!canView) {
    return (
      <div className="page">
        <EmptyState
          icon={<Activity className="h-10 w-10" />}
          title={t('activeLog.noAccessTitle')}
          description={t('activeLog.noAccessBody')}
        />
      </div>
    );
  }

  if (!hasFeature) {
    return (
      <div className="page">
        <header className="mb-6">
          <h1
            className="mb-1 text-2xl font-black tracking-tight text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {t('activeLog.title')}
          </h1>
          <p className="text-[13px] text-[var(--color-xk-text-muted)]">
            {t('activeLog.subtitle')}
          </p>
        </header>
        <PaywallOverlay
          title={t('paywall.activeLog.title')}
          description={t('paywall.activeLog.description')}
          perks={[
            t('paywall.activeLog.perk1'),
            t('paywall.activeLog.perk2'),
            t('paywall.activeLog.perk3'),
          ]}
        >
          <ActiveLogPreview />
        </PaywallOverlay>
      </div>
    );
  }

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const apply = () => {
    setApplied(form);
    setPage(1);
  };
  const reset = () => {
    setForm(EMPTY_FILTER);
    setApplied(EMPTY_FILTER);
    setPage(1);
  };

  return (
    <div className="page">
      <header className="mb-6">
        <h1
          className="mb-1 text-2xl font-black tracking-tight text-[var(--color-xk-lavender)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {t('activeLog.title')}
        </h1>
        <p className="text-[13px] text-[var(--color-xk-text-muted)]">
          {t('activeLog.subtitle')}
        </p>
      </header>

      {/* Filter bar */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
        className="mb-5 grid grid-cols-1 gap-3 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <FilterField label={t('activeLog.filter.search')}>
          <Input
            placeholder={t('activeLog.filter.searchPlaceholder')}
            value={form.q}
            onChange={(e) => setForm({ ...form, q: e.target.value })}
          />
        </FilterField>
        <FilterField label={t('activeLog.filter.action')}>
          <Select
            value={form.action}
            onChange={(e) =>
              setForm({ ...form, action: e.target.value as ActiveLogAction | '' })
            }
          >
            <option value="">{t('activeLog.filter.actionAll')}</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
        </FilterField>
        <FilterField label={t('activeLog.filter.entity')}>
          <Input
            placeholder="e.g. XkEvent"
            value={form.entity}
            onChange={(e) => setForm({ ...form, entity: e.target.value })}
          />
        </FilterField>
        <FilterField label={t('activeLog.filter.userId')}>
          <Input
            type="number"
            inputMode="numeric"
            value={form.userId}
            onChange={(e) => setForm({ ...form, userId: e.target.value })}
          />
        </FilterField>
        <FilterField label={t('activeLog.filter.eventId')}>
          <Input
            type="number"
            inputMode="numeric"
            value={form.eventId}
            onChange={(e) => setForm({ ...form, eventId: e.target.value })}
          />
        </FilterField>
        <FilterField label={t('activeLog.filter.from')}>
          <DatePicker
            mode="date"
            value={form.from}
            max={form.to || undefined}
            onChange={(v) => setForm({ ...form, from: v })}
          />
        </FilterField>
        <FilterField label={t('activeLog.filter.to')}>
          <DatePicker
            mode="date"
            value={form.to}
            min={form.from || undefined}
            onChange={(v) => setForm({ ...form, to: v })}
          />
        </FilterField>
        <div className="flex items-end gap-2">
          <Button type="submit" className="flex-1">
            {t('activeLog.filter.apply')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={reset}
            title={t('activeLog.filter.reset')}
            aria-label={t('activeLog.filter.reset')}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </form>

      {/* List */}
      {listQ.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Activity className="h-10 w-10" />}
          title={t('activeLog.empty.title')}
          description={t('activeLog.empty.body')}
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)]">
            <table className="w-full text-left text-[13px]">
              <thead className="bg-[var(--color-xk-bg-deep)] text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
                <tr>
                  <th className="px-4 py-2.5">{t('activeLog.table.when')}</th>
                  <th className="px-4 py-2.5">{t('activeLog.table.user')}</th>
                  <th className="px-4 py-2.5">{t('activeLog.table.action')}</th>
                  <th className="px-4 py-2.5">{t('activeLog.table.entity')}</th>
                  <th className="px-4 py-2.5">{t('activeLog.table.description')}</th>
                  <th className="w-10 px-2 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-xk-border)]">
                {items.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelected(row)}
                    className="cursor-pointer transition-colors hover:bg-[var(--color-xk-bg-deep)]"
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 text-[12px] text-[var(--color-xk-text-muted)] tabular-nums">
                      {formatDateTime(row.createdAt)}
                    </td>
                    <td className="px-4 py-2.5">
                      <UserCell row={row} />
                    </td>
                    <td className="px-4 py-2.5">
                      <ActionPill action={row.action} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-[var(--color-xk-lavender)]">
                      <span className="font-bold">{row.entity}</span>
                      {row.entityId != null && (
                        <span className="text-[var(--color-xk-text-muted)]">
                          {' '}
                          #{row.entityId}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[480px] truncate px-4 py-2.5 text-[12px] text-[var(--color-xk-text-muted)]">
                      {row.description ?? row.url ?? '—'}
                    </td>
                    <td className="px-2 py-2.5 text-right text-[var(--color-xk-text-muted)]">
                      <Eye className="ml-auto h-4 w-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pager */}
          <div className="mt-4 flex items-center justify-between text-[12px] text-[var(--color-xk-text-muted)]">
            <span>
              {t('activeLog.pager.summary', {
                from: (page - 1) * PER_PAGE + 1,
                to: Math.min(page * PER_PAGE, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                disabled={page <= 1 || listQ.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label={t('activeLog.pager.prev')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="ghost"
                disabled={page >= totalPages || listQ.isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label={t('activeLog.pager.next')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Detail drawer */}
      {selected && (
        <ActiveLogDrawer
          id={selected.id}
          initial={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

// ─── Filter field wrapper ───────────────────────────────────────────────────

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {label}
      </span>
      {children}
    </label>
  );
}

// ─── User cell ──────────────────────────────────────────────────────────────

function UserCell({ row }: { row: ActiveLog }) {
  if (!row.user && !row.userId) {
    return <span className="text-[var(--color-xk-text-muted)]">—</span>;
  }
  const name = row.user
    ? `${row.user.firstname ?? ''} ${row.user.lastname ?? ''}`.trim() ||
      row.user.email
    : `#${row.userId}`;
  return (
    <div className="min-w-0">
      <div className="truncate text-[13px] font-bold text-[var(--color-xk-lavender)]">
        {name}
      </div>
      {row.user?.email && (
        <div className="truncate text-[11px] text-[var(--color-xk-text-muted)]">
          {row.user.email}
        </div>
      )}
    </div>
  );
}

function ActionPill({ action }: { action: ActiveLogAction }) {
  const tone = ACTION_TONE[action];
  return (
    <span
      className="inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider"
      style={{
        color: tone,
        backgroundColor: 'rgba(255,255,255,0.04)',
        border: `1px solid ${tone}`,
        fontFamily: 'var(--font-display)',
      }}
    >
      {action}
    </span>
  );
}

// ─── Detail drawer ──────────────────────────────────────────────────────────

function ActiveLogDrawer({
  id,
  initial,
  onClose,
}: {
  id: number;
  initial: ActiveLog;
  onClose: () => void;
}) {
  const t = useT();
  // Refetch the fresh detail (the row might be stale). Falls back to the
  // listing row while loading so the drawer never flashes empty.
  const detailQ = useQuery({
    queryKey: ['active-log', 'detail', id] as const,
    queryFn: () => activeLogApi.get(id),
    initialData: initial,
    staleTime: 60_000,
  });
  const row = detailQ.data ?? initial;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
    >
      <aside
        className="flex h-full w-full max-w-2xl flex-col border-l border-[var(--color-xk-border)] bg-[var(--color-xk-bg-nav)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-[var(--color-xk-border)] px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <h2
                className="text-base font-extrabold tracking-tight text-[var(--color-xk-lavender)]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {row.entity}
                {row.entityId != null ? ` #${row.entityId}` : ''}
              </h2>
              <ActionPill action={row.action} />
            </div>
            <p className="text-[11px] text-[var(--color-xk-text-muted)]">
              {formatDateTime(row.createdAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-md p-1.5 text-[var(--color-xk-text-muted)] transition-colors hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-xk-lavender)]"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-auto px-5 py-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-[12px]">
            <Field label={t('activeLog.detail.id')} value={row.id} />
            <Field label={t('activeLog.detail.companyId')} value={row.companyId ?? '—'} />
            <Field
              label={t('activeLog.detail.user')}
              value={
                row.user
                  ? `${row.user.firstname ?? ''} ${row.user.lastname ?? ''}`.trim() ||
                    row.user.email
                  : row.userId
                    ? `#${row.userId}`
                    : '—'
              }
            />
            <Field label={t('activeLog.detail.email')} value={row.user?.email ?? '—'} />
            <Field label={t('activeLog.detail.method')} value={row.method ?? '—'} />
            <Field label={t('activeLog.detail.appType')} value={row.appType ?? '—'} />
            <Field label={t('activeLog.detail.eventId')} value={row.eventId ?? '—'} />
            <Field label={t('activeLog.detail.ip')} value={row.ipAddress ?? '—'} />
            <Field
              label={t('activeLog.detail.url')}
              value={row.url ?? '—'}
              wide
              mono
            />
            <Field
              label={t('activeLog.detail.userAgent')}
              value={row.userAgent ?? '—'}
              wide
              mono
            />
            {row.description && (
              <Field
                label={t('activeLog.detail.description')}
                value={row.description}
                wide
              />
            )}
          </dl>

          {row.oldData && (
            <PayloadBlock
              title={t('activeLog.detail.oldData')}
              payload={row.oldData}
            />
          )}
          {row.newData && (
            <PayloadBlock
              title={t('activeLog.detail.newData')}
              payload={row.newData}
            />
          )}
        </div>
      </aside>
    </div>
  );
}

function Field({
  label,
  value,
  wide,
  mono,
}: {
  label: string;
  value: string | number;
  wide?: boolean;
  mono?: boolean;
}) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <dt className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {label}
      </dt>
      <dd
        className={`mt-0.5 break-words text-[13px] text-[var(--color-xk-lavender)] ${
          mono ? 'font-mono text-[11px]' : ''
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function PayloadBlock({
  title,
  payload,
}: {
  title: string;
  payload: Record<string, any>;
}) {
  return (
    <section className="mt-5">
      <h3 className="mb-2 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {title}
      </h3>
      <pre className="max-h-80 overflow-auto rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-3 font-mono text-[11px] leading-relaxed text-[var(--color-xk-lavender)]">
        {JSON.stringify(payload, null, 2)}
      </pre>
    </section>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

// ─── Paywall preview ────────────────────────────────────────────────────────
// Static sample rendered behind the paywall glass so the user can see what the
// feature delivers without exposing real audit data from another tenant.
const PREVIEW_ROWS: Array<{
  id: number;
  when: string;
  user: { name: string; email: string };
  action: ActiveLogAction;
  entity: string;
  entityId: number;
  description: string;
}> = [
  {
    id: 1,
    when: '2026-05-16 14:32:08',
    user: { name: 'María González', email: 'maria@acme.com' },
    action: 'create',
    entity: 'XkEvent',
    entityId: 482,
    description: 'CREATE XkEvent #482 — "Tapatío Fest 2026"',
  },
  {
    id: 2,
    when: '2026-05-16 14:28:51',
    user: { name: 'Carlos Pérez', email: 'carlos@acme.com' },
    action: 'update',
    entity: 'XySurvey',
    entityId: 119,
    description: 'UPDATE XySurvey #119 — wheel probability adjusted',
  },
  {
    id: 3,
    when: '2026-05-16 13:51:02',
    user: { name: 'Sofía Ramírez', email: 'sofia@acme.com' },
    action: 'login',
    entity: 'AuthSession',
    entityId: 7732,
    description: 'LOGIN from 189.144.22.18',
  },
  {
    id: 4,
    when: '2026-05-16 12:15:44',
    user: { name: 'Diego Hernández', email: 'diego@acme.com' },
    action: 'delete',
    entity: 'XkStock',
    entityId: 23,
    description: 'DELETE XkStock #23 — soft-deleted by admin',
  },
];

function ActiveLogPreview() {
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)]">
      <table className="w-full text-left text-[13px]">
        <thead className="bg-[var(--color-xk-bg-deep)] text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          <tr>
            <th className="px-4 py-2.5">Cuándo</th>
            <th className="px-4 py-2.5">Usuario</th>
            <th className="px-4 py-2.5">Acción</th>
            <th className="px-4 py-2.5">Entidad</th>
            <th className="px-4 py-2.5">Descripción</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-xk-border)]">
          {PREVIEW_ROWS.map((row) => (
            <tr key={row.id}>
              <td className="whitespace-nowrap px-4 py-2.5 text-[12px] text-[var(--color-xk-text-muted)] tabular-nums">
                {row.when}
              </td>
              <td className="px-4 py-2.5">
                <div className="text-[13px] font-bold text-[var(--color-xk-lavender)]">
                  {row.user.name}
                </div>
                <div className="text-[11px] text-[var(--color-xk-text-muted)]">
                  {row.user.email}
                </div>
              </td>
              <td className="px-4 py-2.5">
                <ActionPill action={row.action} />
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-[var(--color-xk-lavender)]">
                <span className="font-bold">{row.entity}</span>{' '}
                <span className="text-[var(--color-xk-text-muted)]">
                  #{row.entityId}
                </span>
              </td>
              <td className="max-w-[480px] truncate px-4 py-2.5 text-[12px] text-[var(--color-xk-text-muted)]">
                {row.description}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
